import { classes } from "polytype";
import {
  DataModel,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
} from "../../base/index.js";
import { type AsyncResourceVariant, provideReact } from "../../react/index.js";
import { config } from "../../config/config.js";
import assertObjectFound from "../../base/assertObjectFound.js";
import { Project } from "../../project/index.js";
import type {
  SftpUserAccessLevel,
  SftpUserCreateRequestData,
  SftpUserData,
  SftpUserListItemData,
  SftpUserListQueryData,
  SftpUserUpdateRequestData,
} from "./types.js";
import type { SshUserSshKey } from "../SshUser/index.js";
import { DateTime } from "luxon";
import { arrayRemoveItem } from "../../lib/arrayRemoveItem.js";

export class SftpUser extends ReferenceModel {
  public static ofId(id: string) {
    return new SftpUser(id);
  }

  public static find = provideReact(async (id: string) => {
    const data = await config.behaviors.sftpUser.find(id);
    if (data !== undefined) {
      return new SftpUserDetailed(data);
    }
  });

  public static get = provideReact(async (id: string) => {
    const sftpUser = await this.find(id);
    assertObjectFound(sftpUser, "sftpUser", id);
    return sftpUser;
  });

  public findDetailed = provideReact(
    () => SftpUser.find(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<SftpUserDetailed | undefined>>;

  public getDetailed = provideReact(
    () => SftpUser.get(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<SftpUserDetailed>>;

  public static query(project: Project, query: SftpUserListQueryData = {}) {
    return new SftpUserListQuery(project, query);
  }

  public static async create(
    project: Project,
    data: SftpUserCreateRequestData,
  ) {
    const response = await config.behaviors.sftpUser.create(project.id, data);

    if ("id" in response) {
      return new SftpUser(response.id);
    }

    return response.map((e) => ({
      ...e,
      path:
        e.path.includes("comment") || e.path.includes("key")
          ? "sshKey"
          : e.path,
    }));
  }

  public async update(data: SftpUserUpdateRequestData) {
    const response = await config.behaviors.sftpUser.update(this.id, data);

    if (response) {
      return response.map((e) => ({
        ...e,
        path:
          e.path.includes("comment") || e.path.includes("key")
            ? "sshKey"
            : e.path,
      }));
    }
  }

  public async delete() {
    await config.behaviors.sftpUser.delete(this.id);
  }
}

class SftpUserCommon extends classes(
  DataModel<SftpUserListItemData | SftpUserData>,
  SftpUser,
) {
  public readonly description: string;
  public readonly userName: string;
  public readonly active: boolean;
  public readonly accessLevel: SftpUserAccessLevel;
  public readonly hasFullAccess: boolean;
  public readonly expiresAt?: DateTime;
  public readonly hasPassword?: boolean;
  public readonly hasPublicKeys?: boolean;
  public readonly publicKeys?: SshUserSshKey[];
  public readonly directories?: string[];
  public readonly hasFullDirectoryAccess: boolean;
  public readonly project: Project;

  public constructor(data: SftpUserListItemData | SftpUserData) {
    super([data], [data.id]);
    this.description = data.description;
    this.userName = data.userName;
    this.active = data.active ?? false;
    this.accessLevel = data.accessLevel;
    this.hasFullAccess = data.accessLevel === "full";
    this.expiresAt = data.expiresAt
      ? DateTime.fromISO(data.expiresAt)
      : undefined;
    this.hasPassword = data.hasPassword;
    this.hasPublicKeys = data.publicKeys && data.publicKeys.length > 0;
    this.publicKeys = data.publicKeys;
    this.directories = data.directories ?? ["/"];
    this.hasFullDirectoryAccess =
      this.directories.length === 1 && this.directories[0] === "/";
    this.project = Project.ofId(data.projectId);
  }

  public async deleteSshKey(sshKey: SshUserSshKey) {
    const publicKeys = this.publicKeys;
    if (publicKeys) {
      arrayRemoveItem(
        publicKeys,
        (k) => k.key === sshKey.key && k.comment === sshKey.comment,
      );
    } else return;
    await this.update({ publicKeys });
  }

  public getUserName = provideReact(async () => {
    const project = await this.project.getDetailed();

    return `${this.userName}@${project.shortId}`;
  }, [this.id]);
}

export class SftpUserDetailed extends classes(
  SftpUserCommon,
  DataModel<SftpUserData>,
) {
  public constructor(data: SftpUserData) {
    super([data], [data]);
  }
}

export class SftpUserListItem extends classes(
  SftpUserCommon,
  DataModel<SftpUserListItemData>,
) {
  public constructor(data: SftpUserListItemData) {
    super([data], [data]);
  }
}

export class SftpUserListQuery extends ListQueryModel<SftpUserListQueryData> {
  public readonly project: Project;

  public constructor(project: Project, query: SftpUserListQueryData = {}) {
    super(query, {
      dependencies: [project.id],
    });
    this.project = project;
  }

  public refine(query: SftpUserListQueryData) {
    return new SftpUserListQuery(this.project, {
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { items } = await config.behaviors.sftpUser.list(
      this.project.id,
      this.query,
    );
    return new SftpUserList(
      this.project,
      this.query,
      items.map((d) => new SftpUserListItem(d)),
    );
  }, [this.queryId, "sftpUser"]);
}

export class SftpUserList extends classes(
  SftpUserListQuery,
  ListDataModel<SftpUserListItem>,
) {
  public constructor(
    project: Project,
    query: SftpUserListQueryData,
    sftpUsers: SftpUserListItem[],
  ) {
    super([project, query], [sftpUsers, sftpUsers.length]);
  }
}
